import { useState } from 'react';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import {
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useNotify, useTranslate } from 'react-admin';
import { ApiRequestError } from '@/api/errors';
import { useCanManageAccess } from '@/access/useCanManageAccess';
import { AccessDenied } from '@/components/shell/AccessDenied';
import { EmptyState } from '@/components/shell/EmptyState';
import { ErrorState } from '@/components/shell/ErrorState';
import { LoadingState } from '@/components/shell/LoadingState';
import { PageTitle } from '@/components/shell/PageTitle';
import { PillTabs } from '@/components/shell/PillTabs';
import { SectionHeader } from '@/components/shell/SectionHeader';
import { StatusChip } from '@/components/shell/StatusChip';
import { payApiClient } from '@/api/client';
import { useApiResource } from '@/hooks/useApiResource';
import { TestCardPanel } from '@/features/test-bench/TestCardPanel';
import { gatewayAccountModeTone, gatewayAccountStatusTone } from './domain';
import { GatewayAccountEditDialog } from './GatewayAccountEditDialog';
import { GatewayAccountSecretsPanel } from './GatewayAccountSecretsPanel';

function Field({ label, value }: { label: string; value: string }) {
  return (
    <Stack direction="row" justifyContent="space-between" spacing={2}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500, textAlign: 'right' }}>
        {value}
      </Typography>
    </Stack>
  );
}

/**
 * Gateway account detail against `GET`/`PATCH /v1/admin/gateway-accounts/{id}`
 * (A3's contract), with a `PillTabs` switch to the credentials sub-view
 * (`GatewayAccountSecretsPanel`), mirroring the tenant detail's
 * general/policy split (B3). `AdminGatewayAccount` never carries any
 * credential value — that only ever flows through the credentials tab.
 */
export function GatewayAccountDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const translate = useTranslate();
  const canWrite = useCanManageAccess();
  const gatewayAccountId = id ?? '';
  const [tab, setTab] = useState<'general' | 'credentials'>('general');
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const notify = useNotify();
  const { data, loading, error, reload } = useApiResource(
    () => payApiClient.getAdminGatewayAccount(gatewayAccountId),
    gatewayAccountId,
  );
  // Only fetched once the account is known to carry a preset code (D4) — the
  // presets list is otherwise irrelevant to a hand-created account, and a
  // failure here (e.g. test bench disabled) simply means no test card renders.
  const presets = useApiResource(
    () => (data?.presetCode ? payApiClient.getTestBenchPresets() : Promise.resolve([])),
    `gateway-account-detail-presets-${data?.presetCode ?? ''}`,
  );
  const matchedTestCard = presets.data?.find((preset) => preset.code === data?.presetCode)?.testCard;

  // The platform is the one that decides whether an account may go: it refuses
  // with `conflict` as soon as a payment intent references it. The panel does
  // not try to predict that answer from anything it has loaded — it asks, and
  // translates the refusal into the operator's own wording.
  async function handleDelete() {
    setDeleting(true);
    try {
      await payApiClient.deleteAdminGatewayAccount(gatewayAccountId);
      notify(translate('pay.gatewayAccounts.detail.deleteSuccess'), { type: 'success' });
      setDeleteOpen(false);
      navigate('/gateway-accounts');
    } catch (caught) {
      const inUse = caught instanceof ApiRequestError && caught.errorCode === 'conflict';
      const message = inUse
        ? translate('pay.gatewayAccounts.detail.deleteInUse')
        : caught instanceof ApiRequestError
          ? caught.message
          : translate('pay.errorState.body');
      notify(message, { type: 'error' });
    } finally {
      setDeleting(false);
    }
  }

  const backButton = (
    <IconButton onClick={() => navigate('/gateway-accounts')} aria-label={translate('pay.gatewayAccounts.detail.back')} size="small">
      <ArrowBackOutlinedIcon fontSize="small" />
    </IconButton>
  );

  if (loading) {
    return (
      <Stack spacing={1.5}>
        <Stack direction="row" alignItems="center" spacing={1}>
          {backButton}
        </Stack>
        <Card>
          <LoadingState variant="card" />
        </Card>
      </Stack>
    );
  }

  if (error) {
    return (
      <Stack spacing={1.5}>
        <Stack direction="row" alignItems="center" spacing={1}>
          {backButton}
        </Stack>
        <Card>
          {error.errorCode === 'access_denied' ? (
            <AccessDenied />
          ) : error.errorCode === 'not_found' ? (
            <EmptyState
              title={translate('pay.gatewayAccounts.detail.notFoundTitle')}
              body={translate('pay.gatewayAccounts.detail.notFoundBody')}
            />
          ) : (
            <ErrorState body={error.message} onRetry={reload} />
          )}
        </Card>
      </Stack>
    );
  }

  if (!data) return null;

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" alignItems="center" spacing={1}>
        {backButton}
        <PageTitle
          title={data.alias}
          subtitle={data.id}
          adornment={<StatusChip label={translate(`pay.gatewayAccounts.status.${data.status}`)} tone={gatewayAccountStatusTone(data.status)} />}
        />
      </Stack>

      <SectionHeader
        left={
          <PillTabs
            value={tab}
            onChange={(next) => setTab(next as 'general' | 'credentials')}
            options={[
              { value: 'general', label: translate('pay.gatewayAccounts.detail.tabsGeneral') },
              { value: 'credentials', label: translate('pay.gatewayAccounts.detail.tabsCredentials') },
            ]}
          />
        }
        onRefresh={reload}
      />

      {tab === 'general' ? (
        <Card sx={{ p: 2.5, maxWidth: 640 }}>
          <Stack spacing={1.5}>
            <Field label={translate('pay.gatewayAccounts.detail.fieldTenant')} value={data.tenantCode} />
            <Field label={translate('pay.gatewayAccounts.detail.fieldGateway')} value={translate(`pay.gatewayAccounts.gateway.${data.gateway}`)} />
            <Divider />
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
              <Typography variant="body2" color="text.secondary">
                {translate('pay.gatewayAccounts.detail.fieldMode')}
              </Typography>
              <StatusChip label={translate(`pay.gatewayAccounts.mode.${data.mode}`)} tone={gatewayAccountModeTone(data.mode)} />
            </Stack>
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
              <Typography variant="body2" color="text.secondary">
                {translate('pay.gatewayAccounts.detail.fieldStatus')}
              </Typography>
              <StatusChip label={translate(`pay.gatewayAccounts.status.${data.status}`)} tone={gatewayAccountStatusTone(data.status)} />
            </Stack>
            <Field
              label={translate('pay.gatewayAccounts.detail.fieldCapabilities')}
              value={data.capabilities.length > 0 ? data.capabilities.join(', ') : translate('pay.gatewayAccounts.detail.capabilitiesEmpty')}
            />
            <Field
              label={translate('pay.gatewayAccounts.detail.fieldExternalAccountCode')}
              value={data.externalAccountCode ?? translate('pay.gatewayAccounts.detail.notSet')}
            />
            <Field
              label={translate('pay.gatewayAccounts.detail.fieldExternalTerminalId')}
              value={data.externalTerminalId ?? translate('pay.gatewayAccounts.detail.notSet')}
            />
            <Field
              label={translate('pay.gatewayAccounts.detail.fieldExternalEndpointPrefix')}
              value={data.externalEndpointPrefix ?? translate('pay.gatewayAccounts.detail.notSet')}
            />
            <Field label={translate('pay.gatewayAccounts.detail.fieldCreatedAt')} value={new Date(data.createdAt).toLocaleString('es-ES')} />
            {data.presetCode && (
              <Field label={translate('pay.gatewayAccounts.detail.fieldPresetCode')} value={data.presetCode} />
            )}
            {canWrite && (
              <Stack direction="row" spacing={1} sx={{ pt: 1 }}>
                <Button size="small" variant="outlined" onClick={() => setEditOpen(true)}>
                  {translate('pay.gatewayAccounts.detail.editAction')}
                </Button>
                <Button size="small" variant="outlined" color="error" onClick={() => setDeleteOpen(true)}>
                  {translate('pay.gatewayAccounts.detail.deleteAction')}
                </Button>
              </Stack>
            )}
          </Stack>
        </Card>
      ) : (
        <GatewayAccountSecretsPanel gatewayAccountId={data.id} gateway={data.gateway} />
      )}

      {tab === 'general' && matchedTestCard && (
        <Box sx={{ maxWidth: 640 }}>
          <TestCardPanel testCard={matchedTestCard} />
        </Box>
      )}

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{translate('pay.gatewayAccounts.detail.deleteTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {translate('pay.gatewayAccounts.detail.deleteBody', { alias: data.alias })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>{translate('pay.gatewayAccounts.detail.deleteCancel')}</Button>
          <Button variant="contained" color="error" disabled={deleting} onClick={handleDelete}>
            {translate('pay.gatewayAccounts.detail.deleteConfirm')}
          </Button>
        </DialogActions>
      </Dialog>

      <GatewayAccountEditDialog
        open={editOpen}
        account={data}
        onClose={() => setEditOpen(false)}
        onSaved={() => {
          setEditOpen(false);
          reload();
        }}
      />
    </Stack>
  );
}
